/// Owns the DinoDash instance for GameScreen.
///
/// The game is built once and never rebuilt, so a React re-render cannot
/// restart a run. Theme changes go through setDarkMode and high scores through
/// syncHighScore, exactly as _ensureGame does in game_screen.dart.

import { useEffect, useRef, useState } from 'react';

import { AudioManager } from '../audio/audioManager';
import { DinoDash } from '../dinoDash';
import type { DeathCauseApi } from '../domain/deathCause';
import { gameImagePaths, preloadImages } from '../engine/assets';
import type { GameStatus } from '../gameState';

export interface GameSnapshot {
  status: GameStatus;
  score: number;
  highScore: number;
  /// High score frozen at run start, the progress bar target until beaten.
  runTargetHighScore: number;
  /// Latched when this run beats runTargetHighScore; survives a score sync.
  newHighScoreThisRun: boolean;
}

const initialSnapshot: GameSnapshot = {
  status: 'waiting',
  score: 0,
  highScore: 0,
  runTargetHighScore: 0,
  newHighScoreThisRun: false,
};

export interface GameOverPayload {
  score: number;
  durationMs: number;
  jumpCount: number;
  duckCount: number;
  deathCause: DeathCauseApi;
  wasPersonalBest: boolean;
}

export interface UseDinoDashOptions {
  isDarkMode: boolean;
  highScore: number;
  musicEnabled: boolean;
  sfxEnabled: boolean;
  musicVolume: number;
  sfxVolume: number;
  onGameOver: (payload: GameOverPayload) => void;
}

export interface UseDinoDashResult {
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  game: DinoDash | null;
  snapshot: GameSnapshot;
  audio: AudioManager | null;
}

export function useDinoDash(options: UseDinoDashOptions): UseDinoDashResult {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const gameRef = useRef<DinoDash | null>(null);
  const audioRef = useRef<AudioManager | null>(null);

  const [game, setGame] = useState<DinoDash | null>(null);
  const [snapshot, setSnapshot] = useState<GameSnapshot>(initialSnapshot);

  // Read through a ref so the setup effect never needs them as dependencies,
  // which would tear down and rebuild the game on every render.
  const latest = useRef(options);
  latest.current = options;
  const runStartedAtRef = useRef<number | null>(null);
  const runSubmittedRef = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let disposed = false;
    let instance: DinoDash | null = null;
    let audio: AudioManager | null = null;

    const setup = async (): Promise<void> => {
      await preloadImages(gameImagePaths);
      // Measuring the high-score label before the font resolves would leave
      // it laid out for the fallback metrics.
      await document.fonts?.load('24px "Jersey 10"').catch(() => undefined);
      if (disposed) return;

      audio = new AudioManager();
      await audio.init({
        musicEnabled: latest.current.musicEnabled,
        sfxEnabled: latest.current.sfxEnabled,
        musicVolume: latest.current.musicVolume,
        sfxVolume: latest.current.sfxVolume,
      });
      if (disposed) {
        audio.dispose();
        return;
      }
      audioRef.current = audio;

      instance = new DinoDash({
        canvas,
        audio,
        isDarkMode: latest.current.isDarkMode,
        highScore: latest.current.highScore,
      });
      gameRef.current = instance;

      const sync = (): void => {
        const current = instance!;
        setSnapshot((previous) => {
          const status = current.gameState.status;
          const score = current.gameState.currentScore;
          const highScore = current.gameState.highScore;

          const enteringRun =
            status === 'playing' && (previous.status === 'waiting' || previous.status === 'gameOver');
          const enteringGameOver = status === 'gameOver' && previous.status !== 'gameOver';

          if (enteringRun) {
            runStartedAtRef.current = Date.now();
            runSubmittedRef.current = false;
          }

          const runTargetHighScore = enteringRun ? highScore : previous.runTargetHighScore;
          const crossedHighScore =
            !enteringRun &&
            !previous.newHighScoreThisRun &&
            (status === 'playing' || status === 'paused') &&
            score > runTargetHighScore;
          const newHighScoreThisRun = enteringRun
            ? false
            : previous.newHighScoreThisRun ||
              crossedHighScore ||
              (enteringGameOver && score > runTargetHighScore);

          if (crossedHighScore) {
            audio?.playPoint();
          }

          if (enteringGameOver && !runSubmittedRef.current) {
            runSubmittedRef.current = true;
            const started = runStartedAtRef.current;
            const durationMs = started === null ? 0 : Date.now() - started;
            latest.current.onGameOver({
              score,
              durationMs,
              jumpCount: current.runJumpCount,
              duckCount: current.runDuckCount,
              deathCause: current.runDeathCause ?? 'cactus_small_single',
              wasPersonalBest: newHighScoreThisRun,
            });
          }

          return { status, score, highScore, runTargetHighScore, newHighScoreThisRun };
        });
      };

      const unsubscribe = instance.events.listen(sync);
      instance.start();
      setGame(instance);
      sync();

      cleanupFns.push(unsubscribe);
    };

    const cleanupFns: (() => void)[] = [];
    void setup();

    return () => {
      disposed = true;
      for (const fn of cleanupFns) fn();
      instance?.dispose();
      audio?.dispose();
      gameRef.current = null;
      audioRef.current = null;
    };
  }, []);

  useEffect(() => {
    game?.setDarkMode(options.isDarkMode);
  }, [game, options.isDarkMode]);

  useEffect(() => {
    audioRef.current?.applySettings({
      musicEnabled: options.musicEnabled,
      sfxEnabled: options.sfxEnabled,
      musicVolume: options.musicVolume,
      sfxVolume: options.sfxVolume,
    });
  }, [options.musicEnabled, options.sfxEnabled, options.musicVolume, options.sfxVolume]);

  useEffect(() => {
    if (!game || options.highScore === game.gameState.highScore) return;

    game.syncHighScore(options.highScore);
    setSnapshot((previous) => ({
      ...previous,
      highScore: options.highScore,
      // Only refresh the run target on the home screen. Doing it on game over
      // would hide the "new high score" celebration behind a fresh save.
      ...(previous.status === 'waiting'
        ? { runTargetHighScore: options.highScore, newHighScoreThisRun: false }
        : {}),
    }));
  }, [game, options.highScore]);

  return { canvasRef, game, snapshot, audio: audioRef.current };
}
